// The paint sheet of the engine (R2657, ui-map-interface.md §6.1.14).
//
// The marks of the Sapper part paint on a plain 2D context: a clip, a
// gradient, a filled path, a word (`render/seaobject-kit.ts`). A batch of
// segments, circles and quads states none of these, and the atlas holds
// marks that hold still. So a layer that draws such a look paints it into
// ONE 2D canvas, the engine uploads the canvas as ONE texture, and ONE quad
// draws it over the world rectangle it covers.
//
// **What it costs.** A layer paints only when its picture changed (a row,
// the look, a step of the zoom, a pan past the painted margin, or a look
// that moves), so a still picture costs one quad a frame. A paint uploads
// the painted rectangle and its four mip levels. The canvas grows in steps
// of `STEP_PX` device px, and only a grow makes a new texture.
//
// The texture is the atlas kind (`GfxBackend.atlasTexture`): premultiplied
// on upload, bound on unit 0 of the quad program, so the sheet adds no
// program and no blend state. A lost context restores it from the canvas
// it holds, as it restores the atlas.

import type { AtlasTexture } from './types';
import type { QuadBatch } from './quads';
import type { CameraUniforms } from '../layer';
import type { SheetSink } from '../layers/batches';

/** The step the canvas grows in, device px. */
const STEP_PX = 256;

/** What the sheet needs from the engine. */
export interface PaintSheetDeps {
  /** A texture of the atlas kind over a canvas. */
  texture(canvas: HTMLCanvasElement): AtlasTexture;
  /** A quad batch whose atlas unit binds `bind`. */
  quads(bind: (unit: number) => void): QuadBatch;
}

export class PaintSheet implements SheetSink {
  shown = false;
  private readonly canvas: HTMLCanvasElement | null;
  private readonly ctx: CanvasRenderingContext2D | null;
  private texture: AtlasTexture | null = null;
  private grown = false;
  private readonly quads: QuadBatch;
  /** The painted rectangle in device px, and the world box it covers. */
  private pw = 0;
  private ph = 0;
  private cx = 0;
  private cy = 0;
  private w = 0;
  private h = 0;

  constructor(private readonly deps: PaintSheetDeps) {
    const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = c?.getContext('2d') ?? null;
    this.canvas = ctx ? c : null;
    this.ctx = ctx;
    if (this.canvas) { this.canvas.width = STEP_PX; this.canvas.height = STEP_PX; }
    this.quads = deps.quads((unit) => this.texture?.bind(unit));
  }

  begin(x: number, y: number, w: number, h: number, scale: number): CanvasRenderingContext2D | null {
    const c = this.canvas, ctx = this.ctx;
    if (!c || !ctx || !(w > 0) || !(h > 0) || !(scale > 0)) return null;
    const pw = Math.max(2, Math.ceil(w * scale)), ph = Math.max(2, Math.ceil(h * scale));
    const cw = Math.ceil(pw / STEP_PX) * STEP_PX, ch = Math.ceil(ph / STEP_PX) * STEP_PX;
    if (cw > c.width || ch > c.height) {
      c.width = Math.max(cw, c.width);
      c.height = Math.max(ch, c.height);
      this.grown = true;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.setTransform(scale, 0, 0, scale, -x * scale, -y * scale);
    this.pw = pw; this.ph = ph;
    // The quad's corners sample the first and the last texel centres
    // (`entryUV`), so the box is one texel narrower than the paint.
    this.w = (pw - 1) / scale; this.h = (ph - 1) / scale;
    this.cx = x + pw / (2 * scale); this.cy = y + ph / (2 * scale);
    return ctx;
  }

  end(): void {
    const c = this.canvas;
    if (!c) return;
    if (!this.texture) this.texture = this.deps.texture(c);
    else if (this.grown) this.texture.setSource(c);
    else this.texture.patch(c, 0, 0);
    this.grown = false;
  }

  /** Frees the texture; the engine frees the quad batch. */
  dispose(): void {
    this.texture?.dispose();
    this.texture = null;
  }

  flush(cam: CameraUniforms): number {
    if (!this.shown || !this.texture || this.pw === 0) return 0;
    const c = this.canvas!;
    const q = this.quads;
    q.begin();
    q.quad(this.cx, this.cy, 0, 0, this.w, this.h, 0, 0, 0xffffff, 1,
      0.5 / c.width, 0.5 / c.height, (this.pw - 0.5) / c.width, (this.ph - 0.5) / c.height);
    return q.flush(cam);
  }
}
